// ─────────────────────────────────────────────────────────────
//  MemberPayouts – oeffentliche, secret-key-geschuetzte Auszahlungsseite.
//
//  Wird ueber /payouts/:memberId geoeffnet. Jedes Teammitglied (typisch: die
//  Outreach-Manager) bekommt einen eigenen, teilbaren Link und sieht darauf
//  ausschliesslich SEINE Auszahlungen: was faellig ist, was voraussichtlich
//  kommt und was schon ausgezahlt wurde.
//
//  Der Key kann als ?key=… im Link stecken (Ein-Klick) oder wird im Gate
//  eingegeben. Er wird lokal gemerkt und aus der Adresszeile entfernt, damit
//  der Link beim Weiterleiten eines Screenshots nicht mitwandert.
//  Gelesen wird NIE direkt aus der DB, immer ueber die Cloud Function.
// ─────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { fmtEur, fmtDate, monthLabel, commissionRoleInfo } from '../Admin/paymentEngine';
import './MemberPayouts.css';

const GET_FN_URL =
  process.env.REACT_APP_MEMBER_PAYOUTS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getMemberPayouts';

const keyStorageKey = (memberId) => `mp-key-${memberId}`;

// Holt das Admin-Token (falls eingeloggt) – dann braucht es keinen Key.
function resolveIdToken() {
  return new Promise((resolve) => {
    const current = auth && auth.currentUser;
    if (current) {
      current.getIdToken().then(resolve).catch(() => resolve(null));
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken().then(resolve).catch(() => resolve(null));
      else resolve(null);
    });
  });
}

// Titel einer Zeile: Einmalzahlungen tragen ihre Bezeichnung, alles andere den
// Abrechnungsmonat.
const rowTitle = (row) =>
  row.type === 'onetime' ? (row.label || 'Einmalzahlung') : row.note || monthLabel(row.periodKey);

// Woraus setzt sich der Betrag zusammen? Nur das, was die Zeile erklaert.
const rowDetail = (row) => {
  const parts = [];
  const role = row.commissionRole ? commissionRoleInfo(row.commissionRole) : null;
  if (role) parts.push(role.label);
  if (typeof row.appointments === 'number' && row.appointments > 0) {
    parts.push(`${row.appointments} Termin${row.appointments === 1 ? '' : 'e'}`);
  } else if (row.payType === 'fixed') {
    parts.push('Fixvergütung');
  } else if (row.payType === 'commission' && !role) {
    parts.push('Provision');
  }
  return parts.join(' · ');
};

const PayoutRow = ({ row }) => {
  const detail = rowDetail(row);
  return (
    <li className={`mp-row mp-row-${row.status}`}>
      <div className="mp-row-main">
        <span className="mp-row-company">{row.companyName || 'Ohne Kunde'}</span>
        <span className="mp-row-meta">
          {rowTitle(row)}
          {detail ? ` · ${detail}` : ''}
          {row.status === 'paid' && row.paidAt ? ` · ausgezahlt ${fmtDate(row.paidAt)}` : ''}
          {row.status === 'open' && row.date ? ` · freigegeben ${fmtDate(row.date)}` : ''}
          {row.status === 'expected' && row.date ? ` · Abrechnung ${fmtDate(row.date)}` : ''}
          {row.status === 'cancelled' && row.cancelReason ? ` · Storno: ${row.cancelReason}` : ''}
        </span>
      </div>
      <div className="mp-row-amount">
        {row.appointmentsPending && row.amount <= 0 ? (
          <span className="mp-row-pending">offen</span>
        ) : (
          <span className="mp-row-val">{fmtEur(row.amount)}</span>
        )}
        {row.status === 'expected' && (
          <span className="mp-row-sub">
            {row.appointmentsPending
              ? 'Termine noch nicht erfasst'
              : row.invoiceStatus === 'invoiced'
                ? 'Rechnung beim Kunden'
                : 'Rechnung offen'}
          </span>
        )}
      </div>
    </li>
  );
};

const Section = ({ title, hint, rows, total, tone }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <section className={`mp-section mp-section-${tone}`}>
      <header className="mp-section-head">
        <div>
          <h2 className="mp-section-title">{title}</h2>
          {hint && <p className="mp-section-hint">{hint}</p>}
        </div>
        <span className="mp-section-total">{fmtEur(total)}</span>
      </header>
      <ul className="mp-rows">
        {rows.map((row) => <PayoutRow key={`${row.source}-${row.id}`} row={row} />)}
      </ul>
    </section>
  );
};

const MemberPayouts = () => {
  const { memberId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [phase, setPhase] = useState('loading'); // loading | gate | statement | error
  const [errorMsg, setErrorMsg] = useState('');
  const [gateError, setGateError] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [data, setData] = useState(null);

  // noindex: diese Seiten sollen nicht in Suchmaschinen landen.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const fetchStatement = useCallback(
    async (password) => {
      const token = await resolveIdToken();
      const resp = await fetch(GET_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ memberId, password: password || '' }),
      });
      const payload = await resp.json().catch(() => ({}));
      return { status: resp.status, payload };
    },
    [memberId],
  );

  // Erstes Laden: Key aus dem Link (?key=…), sonst der gemerkte, sonst Gate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fromUrl = (searchParams.get('key') || '').trim();
      const stored = localStorage.getItem(keyStorageKey(memberId)) || '';
      const { status, payload } = await fetchStatement(fromUrl || stored);
      if (cancelled) return;
      if (status === 200 && payload.ok) {
        if (fromUrl) {
          localStorage.setItem(keyStorageKey(memberId), fromUrl);
          // Key aus der Adresszeile nehmen – der Link bleibt trotzdem gültig.
          searchParams.delete('key');
          setSearchParams(searchParams, { replace: true });
        }
        setData(payload);
        setPhase('statement');
      } else if (status === 401 || status === 403) {
        if (stored) localStorage.removeItem(keyStorageKey(memberId));
        setPhase('gate');
      } else {
        setErrorMsg(payload.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
    // searchParams/setSearchParams bewusst nicht in den Abhängigkeiten: das
    // Aufräumen der Adresszeile darf keinen zweiten Abruf auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, fetchStatement]);

  const submitGate = async (e) => {
    e.preventDefault();
    setGateError('');
    const key = keyInput.trim();
    if (!key) { setGateError('Bitte gib deinen Secret Key ein.'); return; }
    const { status, payload } = await fetchStatement(key);
    if (status === 200 && payload.ok) {
      localStorage.setItem(keyStorageKey(memberId), key);
      setData(payload);
      setPhase('statement');
    } else if (status === 403) {
      setGateError('Falscher Key. Bitte prüfe ihn und versuch es erneut.');
    } else if (status === 401) {
      setGateError('Secret Key erforderlich.');
    } else {
      setGateError(payload.error || 'Etwas ist schiefgelaufen. Bitte versuch es erneut.');
    }
  };

  if (phase === 'loading') {
    return (
      <div className="mp-wrap">
        <div className="mp-state">
          <div className="mp-spinner" />
          <p className="mp-state-text">Auszahlungen werden geladen …</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="mp-wrap">
        <div className="mp-state">
          <h1 className="mp-state-title">Etwas ist schiefgelaufen</h1>
          <p className="mp-state-text">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="mp-gate-screen">
        <form className="mp-gate-card" onSubmit={submitGate}>
          <div className="mp-gate-lock" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
          </div>
          <span className="mp-gate-eyebrow">Auszahlungen</span>
          <h1 className="mp-gate-heading">Deine Übersicht ist geschützt</h1>
          <p className="mp-gate-text">Gib den Secret Key ein, den du zu diesem Link bekommen hast.</p>

          <div className={`mp-gate-field ${gateError ? 'mp-gate-field-error' : ''}`}>
            <input
              className="mp-gate-input"
              type="text"
              autoComplete="off"
              autoFocus
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={keyInput}
              onChange={(e) => { setKeyInput(e.target.value); if (gateError) setGateError(''); }}
            />
          </div>
          {gateError && <span className="mp-gate-err">{gateError}</span>}

          <button type="submit" className="mp-gate-cta">Entsperren</button>
          <span className="mp-gate-foot">Vertraulich · nur für dich</span>
        </form>
      </div>
    );
  }

  const t = data.totals || {};
  const roleLine = [data.role, typeof data.memberNo === 'number' ? `MA-${data.memberNo}` : null]
    .filter(Boolean)
    .join(' · ');
  const nothingYet = !data.open.length && !data.expected.length && !data.paid.length;

  return (
    <div className="mp-wrap">
      <div className="mp-sheet">
        <header className="mp-hero">
          <span className="mp-eyebrow">Auszahlungen</span>
          {/* Die Überschrift beantwortet in einer Zeile, worum es geht:
              wer bekommt wie viel noch ausgezahlt. */}
          <h1 className="mp-title">
            {data.name} bekommt noch <span className="mp-title-amount">{fmtEur(t.outstanding)}</span>
          </h1>
          {roleLine && <p className="mp-subtitle">{roleLine}</p>}

          <div className="mp-kpis">
            <div className="mp-kpi mp-kpi-green">
              <span className="mp-kpi-val">{fmtEur(t.open)}</span>
              <span className="mp-kpi-lbl">Fällig</span>
              <span className="mp-kpi-sub">Kunde hat gezahlt</span>
            </div>
            <div className="mp-kpi mp-kpi-amber">
              <span className="mp-kpi-val">{fmtEur(t.expected)}</span>
              <span className="mp-kpi-lbl">Voraussichtlich</span>
              <span className="mp-kpi-sub">Rechnung noch offen</span>
            </div>
            <div className="mp-kpi">
              <span className="mp-kpi-val">{fmtEur(t.paid)}</span>
              <span className="mp-kpi-lbl">Bereits ausgezahlt</span>
              <span className="mp-kpi-sub">{fmtEur(t.paidThisMonth)} diesen Monat</span>
            </div>
          </div>

          {t.appointmentsPending > 0 && (
            <p className="mp-note">
              Bei {t.appointmentsPending} {t.appointmentsPending === 1 ? 'Abrechnung' : 'Abrechnungen'} sind die Termine
              noch nicht erfasst – diese Provision steht erst mit dem Reporting fest.
            </p>
          )}
        </header>

        {nothingYet ? (
          <div className="mp-empty">
            Für dich ist aktuell nichts erfasst. Sobald ein Kunde, dem du zugewiesen bist, abgerechnet wird,
            erscheint hier automatisch deine Auszahlung.
          </div>
        ) : (
          <>
            <Section
              tone="due"
              title="Fällig"
              hint="Der Kunde hat bezahlt – diese Beträge werden überwiesen."
              rows={data.open}
              total={t.open}
            />
            <Section
              tone="expected"
              title="Voraussichtlich"
              hint="Steht fest, sobald der Kunde die zugehörige Rechnung bezahlt hat."
              rows={data.expected}
              total={t.expected}
            />
            <Section
              tone="paid"
              title="Bereits ausgezahlt"
              hint="Deine Historie."
              rows={data.paid}
              total={t.paid}
            />
            <Section
              tone="cancelled"
              title="Storniert"
              hint="Diese Posten kommen nicht zur Auszahlung."
              rows={data.cancelled}
              total={data.cancelled.reduce((s, r) => s + (r.amount || 0), 0)}
            />
          </>
        )}

        <footer className="mp-footer">
          Stand {fmtDate(data.generatedAt)} · Alle Beträge netto. Fragen? Melde dich im Team.
        </footer>
      </div>
    </div>
  );
};

export default MemberPayouts;
